import { WebPlugin } from "@capacitor/core";
// @ts-ignore
import { FaceDetector, FilesetResolver } from "@mediapipe/tasks-vision";
import type {
  ActiveFaceScanResult,
  Face,
  FaceDetectionPlugin,
  FaceLandmark,
  PermissionStatus,
  Point,
  Rect,
  ScanErrorEvent,
  StartActiveScanOptions,
} from "../faceDetection";
import { LandmarkType, LensFacing, FACE_DISTANCE_FACTOR, selectPrimaryFace } from "../faceDetection";
import { IS_WEB_ATTENDANCE_ENABLED } from "../../helpers/utils";

// MediaPipe BlazeFace short-range keypoint indices
const KP_RIGHT_EYE = 0;
const KP_LEFT_EYE = 1;
const KP_NOSE_TIP = 2;
const KP_MOUTH_CENTER = 3;
const KP_RIGHT_EAR = 4;
const KP_LEFT_EAR = 5;

const PUBLIC_ASSET_BASE = (process.env.PUBLIC_URL ?? "").replace(/\/+$/, "");
const FACE_DETECT_MODEL_URL = `${PUBLIC_ASSET_BASE}/models/blaze_face_short_range.tflite`;
const WASM_BASE_PATH = `${PUBLIC_ASSET_BASE}/wasm`;

// Frame throttling
const NORMAL_FRAME_INTERVAL_MS = 120; // ~8fps — enough for smooth scanning
const IDLE_FRAME_INTERVAL_MS = 1000; // ~1fps in idle mode (heat/battery win)
const MOTION_DETECT_INTERVAL_MS = 500;
const MOTION_DIFF_THRESHOLD = 20;

// Face framing thresholds (normalized 0-1). These are intentionally LENIENT:
// the 1s "align" countdown in the UI (plus the multi-frame best-capture
// selection in the components) already shields against capturing a
// poorly-framed face, so we give a comfortable margin for a
// naturally-positioned head (slightly off-centre / close to camera).
const FACE_CENTER_X_MARGIN = 0.30; // ±30% of frame width around centre
const FACE_CENTER_Y_MARGIN = 0.30; // ±30% of frame height around centre
const FACE_EDGE_MARGIN = 0.015;    // allow a face to sit very close to the frame edge

// ⭐ Distance factor — SINGLE SOURCE OF TRUTH is FACE_DISTANCE_FACTOR in
// ../faceDetection.ts. It is used as the default below and re-applied from the
// startActiveScan options on every scan (the components always pass the same
// shared value, which also forwards it to the native Android/iOS plugins).
// NOTE: keep access to FACE_DISTANCE_FACTOR inside functions/lazy — this
// module is imported by faceDetection.ts, so module-init-time access would
// hit a circular-import TDZ error.
let minFaceSizeRatio: number | null = null;
let maxFaceSizeRatio: number | null = null;
const activeMinFaceSizeRatio = () => minFaceSizeRatio ?? FACE_DISTANCE_FACTOR.MIN_FACE_SIZE_RATIO;
const activeMaxFaceSizeRatio = () => maxFaceSizeRatio ?? FACE_DISTANCE_FACTOR.MAX_FACE_SIZE_RATIO;

// Video player state
let videoElement: HTMLVideoElement | null = null;
let canvasElement: HTMLCanvasElement | null = null;
let stream: MediaStream | null = null;
let faceDetector: FaceDetector | null = null;
let running = false;
let rafId: number | null = null;
let frameLoopTimeout: number | null = null;
let lastVideoTime = -1;
let lastFrameTimestamp = 0;
let idleMode = false;

// Motion detection state
let motionDetectionEnabled = false;
let motionDetectInterval: number | null = null;
let prevMotionFrame: ImageData | null = null;

// MediaPipe NormalizedKeypoint shape (not exported from types)
interface NormalizedKP {
  x: number;
  y: number;
  z?: number;
  fromImage?: boolean;
}

function ensureCanvas(): HTMLCanvasElement | null {
  if (canvasElement) return canvasElement;
  canvasElement = document.createElement("canvas");
  return canvasElement;
}

/**
 * Web implementation of the FaceDetectionPlugin.
 * Uses navigator.mediaDevices.getUserMedia() for the webcam,
 * MediaPipe Tasks Vision FaceDetector for face detection, and
 * <video>/<canvas> for preview & capture.
 *
 * This mirrors the native ML Kit / Vision plugin API so the existing
 * FaceClockInOut.tsx and FaceRegistrationModal.tsx components work
 * without major changes.
 */
export class FaceDetectionWeb extends WebPlugin implements FaceDetectionPlugin {
  constructor() {
    super();
  }

  // Override addListener to satisfy the `Promise<PluginListenerHandle> & PluginListenerHandle`
  // contract used by the native plugin registration.
  addListener(eventName: string, listenerFunc: (...args: any[]) => void): any {
    const handle = super.addListener(eventName, listenerFunc);
    const promise = handle.then((h) => h);
    // Attach `.remove` to the promise so callers can call either `await handle`
    // then `.remove()` or directly `handle.remove()`.
    (promise as any).remove = () => {
      handle.then((h) => h.remove());
    };
    return promise;
  }

  async startActiveScan(options: StartActiveScanOptions): Promise<void> {
    this.cleanupVideo();

    // Mark the platform as web so CSS can apply full-screen camera UI
    if (!IS_WEB_ATTENDANCE_ENABLED) {
      document.documentElement.classList.add("web-platform");
      document.body.classList.add("web-platform");
    }

    const lens = options.lensFacing ?? LensFacing.Front;
    const facingMode = lens === LensFacing.Front ? "user" : "environment";

    // 1. Acquire webcam — request the HIGHEST available resolution for
    //    better face recognition quality. Fall back gracefully if the
    //    camera doesn't support the requested resolution.
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920, min: 640 },
          height: { ideal: 1080, min: 480 },
          frameRate: { ideal: 30, min: 15 },
        },
        audio: false,
      });
    } catch (err: any) {
      // Fallback to lower resolution if 1080p fails
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode,
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 },
            frameRate: { ideal: 30, min: 15 },
          },
          audio: false,
        });
      } catch (err2: any) {
        const message =
          err2?.name === "NotAllowedError"
            ? "Camera permission denied. Please enable camera access in your browser."
            : err2?.message || "Unable to access camera.";
        this.notifyListeners("scanError", { message } satisfies ScanErrorEvent);
        throw err2;
      }
    }

    // 2. Create & inject video element for preview
    videoElement = document.createElement("video");
    videoElement.setAttribute("playsinline", "true");
    videoElement.setAttribute("autoplay", "true");
    videoElement.setAttribute("muted", "true");
    videoElement.classList.add("webcam-preview");
    videoElement.srcObject = stream;
    videoElement.style.width = "100%";
    videoElement.style.height = "100%";
    videoElement.style.objectFit = "cover";
    videoElement.style.pointerEvents = "none";
    videoElement.play().catch(() => {});

    this.injectVideoIntoFrame();

    // 3. Load MediaPipe FaceDetector (try GPU first, fall back to CPU)
    try {
      await this.initFaceDetector("GPU");
    } catch (gpuErr) {
      console.warn(
        "[FaceDetectionWeb] GPU delegate failed, falling back to CPU:",
        gpuErr,
      );
      try {
        await this.initFaceDetector("CPU");
      } catch (cpuErr: any) {
        this.cleanupVideo();
        const message = `Failed to load face detector: ${
          cpuErr?.message || "Unknown error"
        }`;
        this.notifyListeners("scanError", { message } satisfies ScanErrorEvent);
        throw cpuErr;
      }
    }

    // 4. Apply the distance factor for this scan session. Falls back to the
    //    shared FACE_DISTANCE_FACTOR single-source-of-truth defaults when the
    //    caller doesn't provide one.
    minFaceSizeRatio = options.minFaceSizeRatio ?? FACE_DISTANCE_FACTOR.MIN_FACE_SIZE_RATIO;
    maxFaceSizeRatio = options.maxFaceSizeRatio ?? FACE_DISTANCE_FACTOR.MAX_FACE_SIZE_RATIO;

    // 5. Start processing loop
    running = true;
    lastVideoTime = -1;
    lastFrameTimestamp = 0;
    this.startFrameLoop();
  }

  private async initFaceDetector(delegate: "CPU" | "GPU"): Promise<void> {
    const vision = await FilesetResolver.forVisionTasks(WASM_BASE_PATH);
    faceDetector = await FaceDetector.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: FACE_DETECT_MODEL_URL,
        delegate,
      },
      runningMode: "VIDEO",
      minDetectionConfidence: 0.25,
    });
  }

  private injectVideoIntoFrame(): void {
    if (!videoElement) return;
    // Place the video inside .camera-frame so it appears through the oval hole.
    const frame = document.querySelector(".camera-frame");
    if (frame) {
      frame.insertBefore(videoElement, frame.firstChild);
    } else {
      document.body.appendChild(videoElement);
      videoElement.style.position = "fixed";
      videoElement.style.top = "0";
      videoElement.style.left = "0";
      videoElement.style.width = "100%";
      videoElement.style.height = "100%";
      videoElement.style.objectFit = "cover";
      videoElement.style.zIndex = "1";
      videoElement.style.pointerEvents = "none";
    }
  }

  private startFrameLoop(): void {
    const loop = () => {
      if (!running || !videoElement || !faceDetector) return;

      const now = performance.now();
      const frameInterval = idleMode
        ? IDLE_FRAME_INTERVAL_MS
        : NORMAL_FRAME_INTERVAL_MS;

      if (now - lastFrameTimestamp >= frameInterval) {
        lastFrameTimestamp = now;

        if (
          videoElement.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA &&
          videoElement.videoWidth > 0 &&
          videoElement.currentTime !== lastVideoTime
        ) {
          lastVideoTime = videoElement.currentTime;

          try {
            const result = faceDetector.detectForVideo(videoElement, now);

            if (!result || result.detections.length === 0) {
              this.notifyListeners("faceScanned", {
                face: null,
                facesCount: 0,
                frameWidth: videoElement.videoWidth || 0,
                frameHeight: videoElement.videoHeight || 0,
              } satisfies ActiveFaceScanResult);
            } else {
              const frameWidth = videoElement.videoWidth || 0;
              const frameHeight = videoElement.videoHeight || 0;

              // Map ALL detections and apply the shared multi-face policy
              // (FACE_MULTI_FACE_POLICY): the most prominent (largest =
              // closest) face is the scan candidate; background /
              // half-visible faces are ignored unless comparably prominent.
              const mappedFaces = result.detections.map((detection) =>
                this.mapDetectionToFace(detection),
              );
              const { primary } = selectPrimaryFace(mappedFaces, frameWidth, frameHeight);
              const face = primary ?? mappedFaces[0];

              // Only emit a face that is visible, properly positioned, and
              // within the configured distance factor range.
              const { positioned, tooFar } = this.evaluateFacePosition(face);
              if (positioned) {
                this.notifyListeners("faceScanned", {
                  face,
                  faces: mappedFaces,
                  facesCount: mappedFaces.length,
                  frameWidth,
                  frameHeight,
                } satisfies ActiveFaceScanResult);
              } else {
                // Face detected but rejected — emit null so the UI shows
                // "align face" / "move closer" guidance instead of capturing.
                this.notifyListeners("faceScanned", {
                  face: null,
                  faces: mappedFaces,
                  facesCount: mappedFaces.length,
                  frameWidth,
                  frameHeight,
                  ...(tooFar ? { tooFar: true } : {}),
                } satisfies ActiveFaceScanResult);
              }
            }
          } catch (err) {
            console.error("[FaceDetectionWeb] Detection error:", err);
          }
        } else {
          this.notifyListeners("faceScanned", {
            face: null,
            facesCount: 0,
          } satisfies ActiveFaceScanResult);
        }
      }

      // HEAT/BATTERY OPTIMIZATION: schedule the next poll with setTimeout
      // instead of requestAnimationFrame. requestAnimationFrame still fires
      // ~60 times per second even when the interval check bails out, which
      // keeps the main thread busy for no reason while idle. Falling back to
      // a real timer that only wakes ~2-8 times per second (matching the
      // interval) dramatically cuts main-thread wake-ups in idle mode.
      frameLoopTimeout = window.setTimeout(loop, frameInterval);
    };

    frameLoopTimeout = window.setTimeout(loop, NORMAL_FRAME_INTERVAL_MS);
  }

  /**
   * Validate visibility, framing and the distance factor without judging
   * camera/image quality.
   *
   * Distance factor: a face's bounding-box height relative to the frame height
   * is inversely proportional to how far the person is from the camera. Faces
   * smaller than `minFaceSizeRatio` (someone walking by farther away) are
   * rejected, and reported with `tooFar: true` so the UI can guide the user.
   */
  private evaluateFacePosition(face: Face): { positioned: boolean; tooFar: boolean } {
    if (!videoElement) return { positioned: false, tooFar: false };

    const vw = videoElement.videoWidth || 1;
    const vh = videoElement.videoHeight || 1;

    const box = face.bounds;
    const boxWidth = box.right - box.left;
    const boxHeight = box.bottom - box.top;

    if (boxWidth <= 0 || boxHeight <= 0) return { positioned: false, tooFar: false };

    // Normalized face size (relative to frame height)
    const faceSizeRatio = boxHeight / vh;
    const faceCenterX = (box.left + box.right) / 2 / vw;
    const faceCenterY = (box.top + box.bottom) / 2 / vh;
    const isInsideFrame =
      box.left / vw >= FACE_EDGE_MARGIN &&
      box.top / vh >= FACE_EDGE_MARGIN &&
      box.right / vw <= 1 - FACE_EDGE_MARGIN &&
      box.bottom / vh <= 1 - FACE_EDGE_MARGIN;
    const isCentered =
      Math.abs(faceCenterX - 0.5) <= FACE_CENTER_X_MARGIN &&
      Math.abs(faceCenterY - 0.5) <= FACE_CENTER_Y_MARGIN;

    const requiredLandmarks = [
      LandmarkType.RightEye,
      LandmarkType.LeftEye,
      LandmarkType.NoseBase,
      LandmarkType.MouthLeft,
    ];
    const hasRequiredLandmarks = requiredLandmarks.every((type) =>
      face.landmarks?.some((landmark) => landmark.type === type),
    );

    // Distance factor (lower bound): face must be large enough (close enough)
    const isLargeEnough = faceSizeRatio >= activeMinFaceSizeRatio();

    // Distance factor (upper bound): face must not be too large (too close)
    const isNotTooLarge = faceSizeRatio <= activeMaxFaceSizeRatio();

    const positioned =
      isInsideFrame && isCentered && isLargeEnough && isNotTooLarge && hasRequiredLandmarks;

    // "Too far" = the ONLY failing check is the lower distance bound — the
    // face is well-framed and identifiable, just farther away than allowed.
    const tooFar =
      !isLargeEnough && isInsideFrame && isCentered && isNotTooLarge && hasRequiredLandmarks;

    return { positioned, tooFar };
  }

  /**
   * Convert a MediaPipe Detection into the same Face shape
   * the native ML Kit / Vision plugin produces.
   */
  private mapDetectionToFace(detection: any): Face {
    const box = detection.boundingBox || {
      originX: 0,
      originY: 0,
      width: 0,
      height: 0,
    };
    const kps: NormalizedKP[] = detection.keypoints || [];

    const bounds: Rect = {
      left: box.originX,
      top: box.originY,
      right: box.originX + box.width,
      bottom: box.originY + box.height,
    };

    const landmarks: FaceLandmark[] = [];
    const addLandmark = (type: number, kpIndex: number) => {
      const pt = this.getLandmarkPoint(kps, kpIndex);
      if (pt) {
        const w = videoElement?.videoWidth || 1;
        const h = videoElement?.videoHeight || 1;
        landmarks.push({
          type,
          position: { x: pt.x * w, y: pt.y * h },
        });
      }
    };

    // Map MediaPipe keypoints to ML Kit landmark types
    addLandmark(LandmarkType.RightEye, KP_RIGHT_EYE);
    addLandmark(LandmarkType.LeftEye, KP_LEFT_EYE);
    addLandmark(LandmarkType.NoseBase, KP_NOSE_TIP);
    addLandmark(LandmarkType.MouthLeft, KP_MOUTH_CENTER);
    addLandmark(LandmarkType.MouthRight, KP_MOUTH_CENTER);
    addLandmark(LandmarkType.RightEar, KP_RIGHT_EAR);
    addLandmark(LandmarkType.LeftEar, KP_LEFT_EAR);

    const angles = this.estimateHeadAngles(kps);
    const confidence = detection.categories?.[0]?.score ?? 0;

    return {
      bounds,
      landmarks,
      contours: [],
      trackingId: 0,
      headEulerAngleX: angles.x,
      headEulerAngleY: angles.y,
      headEulerAngleZ: angles.z,
      leftEyeOpenProbability: confidence,
      rightEyeOpenProbability: confidence,
      smilingProbability: 0,
    } as Face;
  }

  private getLandmarkPoint(
    kps: NormalizedKP[] | undefined,
    index: number,
  ): Point | null {
    if (!kps || !kps[index]) return null;
    return { x: kps[index].x, y: kps[index].y };
  }

  /**
   * Estimate Euler angles from BlazeFace keypoints.
   * Values are in degrees, near 0 when facing straight.
   */
  private estimateHeadAngles(kps: NormalizedKP[]): {
    x: number;
    y: number;
    z: number;
  } {
    if (!kps || kps.length < 6) return { x: 0, y: 0, z: 0 };

    const leftEye = kps[KP_LEFT_EYE];
    const rightEye = kps[KP_RIGHT_EYE];
    const nose = kps[KP_NOSE_TIP];
    if (!leftEye || !rightEye || !nose) return { x: 0, y: 0, z: 0 };

    const eyeMidX = (leftEye.x + rightEye.x) / 2;
    const eyeMidY = (leftEye.y + rightEye.y) / 2;
    const eyeDist =
      Math.sqrt(
        Math.pow((leftEye.x - rightEye.x) * 100, 2) +
          Math.pow((leftEye.y - rightEye.y) * 100, 2),
      ) || 1;

    const yaw = (((nose.x - eyeMidX) * 100) / eyeDist) * 25;
    const pitch = (((nose.y - eyeMidY) * 100) / eyeDist) * 25;
    const roll =
      Math.atan2(
        (rightEye.y - leftEye.y) * 100,
        (rightEye.x - leftEye.x) * 100,
      ) *
      (180 / Math.PI);

    return { x: pitch, y: yaw, z: roll };
  }

  async stopScan(): Promise<void> {
    running = false;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (frameLoopTimeout !== null) {
      window.clearTimeout(frameLoopTimeout);
      frameLoopTimeout = null;
    }
    this.stopMotionDetectionLoop();
    this.cleanupVideo();
  }

  private cleanupVideo(): void {
    if (videoElement) {
      videoElement.srcObject = null;
      videoElement.remove();
      videoElement = null;
    }
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    if (faceDetector) {
      try {
        faceDetector.close();
      } catch {
        // ignore
      }
      faceDetector = null;
    }
    canvasElement = null;

    // Remove web-platform classes now that the camera is gone
    document.documentElement.classList.remove("web-platform");
    document.body.classList.remove("web-platform");
  }

  async captureFrame(): Promise<{ image: string }> {
    if (
      !videoElement ||
      !videoElement.videoWidth ||
      !videoElement.videoHeight
    ) {
      throw new Error("No active video stream. Start scan first.");
    }

    const vw = videoElement.videoWidth;
    const vh = videoElement.videoHeight;

    // Standardize to 480x640 portrait (3:4 aspect ratio) matching native
    // Android & iOS camera frames. We keep this resolution to stay within
    // the backend's default 100kb JSON body limit for the users route
    // (the employee-details endpoint doesn't have the 20mb limit that
    // attendance routes have). The image enhancement + multi-frame selection
    // still significantly improve quality at this resolution.
    const TARGET_WIDTH = 480;
    const TARGET_HEIGHT = 640;
    const TARGET_ASPECT = TARGET_WIDTH / TARGET_HEIGHT; // 0.75

    const canvas = ensureCanvas();
    if (!canvas) throw new Error("Cannot create canvas.");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Cannot get 2D canvas context.");

    canvas.width = TARGET_WIDTH;
    canvas.height = TARGET_HEIGHT;

    // CRITICAL FIX: NEVER rotate the web video frame.
    // Desktop webcams deliver landscape video (e.g. 640x480) where the face
    // is ALREADY upright. Mobile browsers also auto-orient the video so the
    // face is upright (portrait OR landscape depending on how the device is
    // held). Rotating would make the face sideways!
    // The correct approach is to center-CROP the video to a portrait 3:4
    // frame — the face stays upright, we just crop the sides.
    let cropWidth: number;
    let cropHeight: number;

    const currentAspect = vw / vh;
    if (currentAspect > TARGET_ASPECT) {
      // Wider than 3:4 (e.g. landscape webcam 640x480) — fit height, center crop width
      cropHeight = vh;
      cropWidth = Math.round(vh * TARGET_ASPECT);
    } else {
      // Taller than 3:4 (portrait phone video) — fit width, center crop height
      cropWidth = vw;
      cropHeight = Math.round(vw / TARGET_ASPECT);
    }

    const cropX = Math.max(0, Math.round((vw - cropWidth) / 2));
    const cropY = Math.max(0, Math.round((vh - cropHeight) / 2));

    // Natural (un-mirrored) upright portrait frame matching native Android & iOS capture
    ctx.drawImage(
      videoElement,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      TARGET_WIDTH,
      TARGET_HEIGHT
    );

    // Apply image enhancement to improve face recognition quality on
    // low-quality webcams: slight contrast boost + sharpening.
    this.enhanceImage(ctx, TARGET_WIDTH, TARGET_HEIGHT);

    // Use 0.9 quality to keep the base64 payload within the backend's
    // default 100kb JSON body limit for the users/employee-details route.
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    return { image: dataUrl };
  }

  /**
   * Applies contrast enhancement and sharpening to improve face recognition
   * quality on low-quality webcams. This helps face-api.js extract more
   * accurate 128-D descriptors that match better against native ML Kit
   * signatures.
   */
  private enhanceImage(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): void {
    try {
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      // 1. Contrast stretch — map the darkest pixel to 0 and brightest to 255
      //    to improve dynamic range on washed-out webcam images.
      let min = 255;
      let max = 0;
      let lumSum = 0;
      const pxCount = data.length / 4;
      for (let i = 0; i < data.length; i += 4) {
        const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        if (lum < min) min = lum;
        if (lum > max) max = lum;
        lumSum += lum;
      }
      const avgLum = lumSum / pxCount;
      const range = max - min;
      if (range > 0 && range < 200) {
        const scale = 255 / range;
        const offset = -min * scale;
        for (let i = 0; i < data.length; i += 4) {
          data[i] = Math.max(0, Math.min(255, data[i] * scale + offset));
          data[i + 1] = Math.max(0, Math.min(255, data[i + 1] * scale + offset));
          data[i + 2] = Math.max(0, Math.min(255, data[i + 2] * scale + offset));
        }
      }

      // 1b. DARK-FRAME GAMMA LIFT — when the webcam feed is dim (e.g. night or
      //     warehouse lighting), stretch the dark tones up so face-api.js can see
      //     the facial landmarks. gamma < 1 lifts shadows more than highlights,
      //     which brightens a dark face without overexposing an already-bright one.
      if (avgLum < 70) {
        const gamma = 0.6; // (<1 = brighter shadows)
        for (let i = 0; i < data.length; i += 4) {
          data[i] = Math.max(0, Math.min(255, (Math.pow(data[i] / 255, gamma)) * 255));
          data[i + 1] = Math.max(0, Math.min(255, (Math.pow(data[i + 1] / 255, gamma)) * 255));
          data[i + 2] = Math.max(0, Math.min(255, (Math.pow(data[i + 2] / 255, gamma)) * 255));
        }
      }

      // 2. Light sharpening using a 3x3 unsharp mask (simplified)
      //    This enhances facial feature edges (eyes, nose, mouth) which are
      //    critical for face-api.js landmark detection and descriptor quality.
      const sharpened = new Uint8ClampedArray(data);
      const w = width;
      const h = height;
      const strength = 0.3; // 30% sharpening
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const idx = (y * w + x) * 4;
          for (let c = 0; c < 3; c++) {
            const center = data[idx + c];
            const up = data[((y - 1) * w + x) * 4 + c];
            const down = data[((y + 1) * w + x) * 4 + c];
            const left = data[(y * w + (x - 1)) * 4 + c];
            const right = data[(y * w + (x + 1)) * 4 + c];
            const avg = (up + down + left + right) / 4;
            sharpened[idx + c] = center + (center - avg) * strength;
          }
        }
      }
      imageData.data.set(sharpened);
      ctx.putImageData(imageData, 0, 0);
    } catch (err) {
      // If enhancement fails (e.g. canvas tainted), just use the raw frame
      console.warn("[FaceDetectionWeb] Image enhancement skipped:", err);
    }
  }
  async setIdleMode(options: { enabled: boolean }): Promise<void> {
    idleMode = options.enabled;
  }

  async setMotionDetection(options: { enabled: boolean }): Promise<void> {
    if (options.enabled) {
      motionDetectionEnabled = true;
      this.startMotionDetectionLoop();
    } else {
      motionDetectionEnabled = false;
      this.stopMotionDetectionLoop();
    }
  }

  private startMotionDetectionLoop(): void {
    if (motionDetectInterval !== null) return;
    prevMotionFrame = null;

    motionDetectInterval = window.setInterval(() => {
      if (
        !videoElement ||
        videoElement.readyState < 2 ||
        !motionDetectionEnabled
      ) {
        return;
      }

      const canvas = ensureCanvas();
      if (!canvas) return;
      canvas.width = 160;
      canvas.height = 120;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(videoElement, 0, 0, 160, 120);

      let current: ImageData;
      try {
        current = ctx.getImageData(0, 0, 160, 120);
      } catch {
        return;
      }

      if (prevMotionFrame) {
        let diff = 0;
        const prev = prevMotionFrame.data;
        const curr = current.data;
        for (let i = 0; i < curr.length; i += 4) {
          diff += Math.abs(curr[i] - prev[i]);
          diff += Math.abs(curr[i + 1] - prev[i + 1]);
          diff += Math.abs(curr[i + 2] - prev[i + 2]);
        }
        const avgDiff = diff / (160 * 120 * 3);
        if (avgDiff > MOTION_DIFF_THRESHOLD) {
          this.notifyListeners("motionDetected", {
            trigger: "frame_diff",
            distance: avgDiff,
          });
        }
      }
      prevMotionFrame = current;
    }, MOTION_DETECT_INTERVAL_MS);
  }

  private stopMotionDetectionLoop(): void {
    if (motionDetectInterval !== null) {
      clearInterval(motionDetectInterval);
      motionDetectInterval = null;
    }
    prevMotionFrame = null;
  }

  async checkPermissions(): Promise<PermissionStatus> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return { camera: "denied" };
    }

    try {
      // @ts-ignore - PermissionName types don't include "camera" in some TS versions
      const permStatus = await navigator.permissions.query({ name: "camera" });
      if (permStatus.state === "granted") return { camera: "granted" };
      if (permStatus.state === "denied") return { camera: "denied" };
      return { camera: "prompt" };
    } catch {
      return { camera: "prompt" };
    }
  }

  async requestPermissions(): Promise<PermissionStatus> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      s.getTracks().forEach((t) => t.stop());
      return { camera: "granted" };
    } catch {
      return { camera: "denied" };
    }
  }
}
